<script>
export default {
  name: "OrderStatisticsPanel",
  props: {
    stats: {
      type: Object,
      default: () => null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showTrend: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeView: "payment",
    };
  },
  computed: {
    summary() {
      return (this.stats && this.stats.summary) || {};
    },
    byPayment() {
      return (this.stats && this.stats.by_payment) || [];
    },
    byStatus() {
      return (this.stats && this.stats.by_status) || [];
    },
    trend() {
      return (this.stats && this.stats.trend) || [];
    },
    hasData() {
      return this.byPayment.length > 0 || this.byStatus.length > 0 || this.trend.length > 0 || this.summary.total_count > 0;
    },
    summaryCards() {
      return [
        { key: "total_orders", count: this.summary.total_count, amount: this.summary.paid_amount, volume: this.summary.paid_volume, color: "primary" },
        { key: "paid", count: this.summary.paid_count, amount: this.summary.paid_amount, volume: this.summary.paid_volume, color: "success" },
        { key: "unpaid", count: this.summary.unpaid_count, amount: this.summary.unpaid_amount, volume: this.summary.unpaid_volume, color: "warning" },
        { key: "refund", count: this.summary.refund_count, amount: this.summary.refund_amount, volume: this.summary.refund_volume, color: "danger" },
      ];
    },
    paymentChartSeries() {
      return [
        {
          name: this.$t("statistics.sales.paid"),
          data: this.byPayment.map((item) => item.paid_count || 0),
        },
        {
          name: this.$t("statistics.sales.unpaid"),
          data: this.byPayment.map((item) => item.unpaid_count || 0),
        },
        {
          name: this.$t("statistics.sales.refund"),
          data: this.byPayment.map((item) => item.refund_count || 0),
        },
      ];
    },
    paymentChartOptions() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
          height: this.compact ? 260 : 320,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.byPayment.map((item) => this.paywayLabel(item)),
          labels: {
            rotate: -35,
            trim: true,
          },
        },
        colors: ["#1cbb8c", "#eeb902", "#f46a6a"],
        legend: { position: "top" },
        tooltip: {
          y: {
            formatter: (value) => this.chartCountFormatter(value),
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => this.chartCountFormatter(value),
          },
        },
      };
    },
    statusChartSeries() {
      return this.byStatus.map((item) => item.count || 0);
    },
    statusChartOptions() {
      return {
        chart: {
          type: "donut",
          height: this.compact ? 260 : 320,
        },
        labels: this.byStatus.map((item) => this.statusLabel(item)),
        colors: ["#eeb902", "#1cbb8c", "#f46a6a", "#5664d2"],
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        tooltip: {
          y: {
            formatter: (value) => this.chartCountFormatter(value),
          },
        },
        plotOptions: {
          pie: {
            dataLabels: {
              formatter: (val, opts) =>
                this.formatCount(opts.w.globals.series[opts.seriesIndex]),
            },
          },
        },
      };
    },
    trendChartSeries() {
      const sortedTrend = this.sortedTrend;
      return [
        {
          name: this.$t("statistics.sales.total_orders"),
          type: "column",
          data: sortedTrend.map((item) => item.total_count || 0),
        },
        {
          name: this.$t("statistics.sales.paid"),
          type: "line",
          data: sortedTrend.map((item) => item.paid_count || 0),
        },
        {
          name: this.$t("statistics.sales.unpaid"),
          type: "line",
          data: sortedTrend.map((item) => item.unpaid_count || 0),
        },
        {
          name: this.$t("statistics.sales.refund"),
          type: "line",
          data: sortedTrend.map((item) => item.refund_count || 0),
        },
      ];
    },
    trendChartOptions() {
      return {
        chart: {
          height: this.compact ? 260 : 320,
          type: "line",
          toolbar: { show: false },
        },
        stroke: {
          width: [0, 3, 3, 3],
          curve: "smooth",
        },
        plotOptions: {
          bar: {
            columnWidth: "35%",
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.sortedTrend.map((item) => item.date || item.month),
        },
        colors: ["#5664d2", "#1cbb8c", "#eeb902", "#f46a6a"],
        legend: { position: "top" },
        tooltip: {
          y: {
            formatter: (value) => this.chartCountFormatter(value),
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => this.chartCountFormatter(value),
          },
        },
      };
    },
    sortedTrend() {
      return [...this.trend].reverse();
    },
  },
  methods: {
    paywayLabel(item) {
      const name = item.payway_name || "unknown";
      const key = `device.orders.payment.${name}`;
      return this.$te(key) ? this.$t(key) : name;
    },
    statusLabel(item) {
      const map = {
        paid: "device.orders.orderpay",
        unpaid: "device.orders.nopay",
        refund: "device.orders.refuntrue",
      };
      const key = map[item.status_name] || "device.orders.allstatus";
      return this.$t(key);
    },
    parseNumber(value) {
      if (value === undefined || value === null || value === "") {
        return 0;
      }
      if (typeof value === "string") {
        value = value.replace(/,/g, "");
      }
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    },
    formatCount(value) {
      return Math.floor(this.parseNumber(value)).toLocaleString("en-US");
    },
    formatAmount(value) {
      return this.parseNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatVolume(value) {
      return `${this.formatCount(value)} ML`;
    },
    chartCountFormatter(value) {
      return this.formatCount(value);
    },
  },
};
</script>

<template>
  <div class="order-statistics-panel">
    <el-empty v-if="!hasData" :description="$t('dashboard.nostatistics')"></el-empty>
    <template v-else>
      <div class="row mb-3">
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="col-6 col-lg-3 mb-3"
        >
          <div class="card border-0 shadow-sm h-100 summary-card">
            <div class="card-body">
              <div :class="['text-' + card.color, 'small mb-1']">
                {{ $t("statistics.sales." + card.key) }}
              </div>
              <div class="h5 mb-1">{{ formatCount(card.count) }}</div>
              <div class="text-muted small">
                {{ $t("statistics.sales.amount") }}: {{ formatAmount(card.amount) }}
              </div>
              <div class="text-muted small">
                {{ $t("statistics.sales.total") }}: {{ formatVolume(card.volume) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <el-tabs v-if="!compact" v-model="activeView">
        <el-tab-pane :label="$t('statistics.sales.by_payment')" name="payment"></el-tab-pane>
        <el-tab-pane :label="$t('statistics.sales.by_status')" name="status"></el-tab-pane>
        <el-tab-pane v-if="showTrend" :label="$t('statistics.sales.by_trend')" name="trend"></el-tab-pane>
      </el-tabs>

      <div v-if="compact || activeView === 'payment'" class="mb-4">
        <h6 v-if="compact" class="mb-3">{{ $t("statistics.sales.by_payment") }}</h6>
        <div class="row">
          <div class="col-lg-7 mb-3">
            <apexchart
              v-if="byPayment.length"
              type="bar"
              :height="compact ? 260 : 320"
              :series="paymentChartSeries"
              :options="paymentChartOptions"
            ></apexchart>
          </div>
          <div class="col-lg-5">
            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t("device.orders.paytype") }}</th>
                    <th>{{ $t("statistics.sales.total_orders") }}</th>
                    <th>{{ $t("statistics.sales.paid") }}</th>
                    <th>{{ $t("statistics.sales.unpaid") }}</th>
                    <th>{{ $t("statistics.sales.refund") }}</th>
                    <th>{{ $t("statistics.sales.amount") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in byPayment" :key="'pay-' + index">
                    <td>{{ paywayLabel(item) }}</td>
                    <td>{{ formatCount(item.total_count) }}</td>
                    <td>{{ formatCount(item.paid_count) }}</td>
                    <td>{{ formatCount(item.unpaid_count) }}</td>
                    <td>{{ formatCount(item.refund_count) }}</td>
                    <td>{{ formatAmount(item.paid_amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!compact && activeView === 'status'" class="mb-4">
        <div class="row">
          <div class="col-lg-5 mb-3">
            <apexchart
              v-if="byStatus.length"
              type="donut"
              :height="320"
              :series="statusChartSeries"
              :options="statusChartOptions"
            ></apexchart>
          </div>
          <div class="col-lg-7">
            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t("device.orders.orderstatus") }}</th>
                    <th>{{ $t("statistics.sales.count") }}</th>
                    <th>{{ $t("statistics.sales.amount") }}</th>
                    <th>{{ $t("statistics.sales.total") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in byStatus" :key="'status-' + index">
                    <td>{{ statusLabel(item) }}</td>
                    <td>{{ formatCount(item.count) }}</td>
                    <td>{{ formatAmount(item.amount) }}</td>
                    <td>{{ formatVolume(item.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showTrend && (compact || activeView === 'trend')" class="mb-2">
        <h6 v-if="compact" class="mb-3">{{ $t("statistics.sales.by_trend") }}</h6>
        <div class="row">
          <div class="col-12 mb-3">
            <apexchart
              v-if="trend.length"
              type="line"
              :height="compact ? 260 : 320"
              :series="trendChartSeries"
              :options="trendChartOptions"
            ></apexchart>
          </div>
          <div class="col-12">
            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t("statistics.sales.date") }}</th>
                    <th>{{ $t("statistics.sales.total_orders") }}</th>
                    <th>{{ $t("statistics.sales.paid") }}</th>
                    <th>{{ $t("statistics.sales.unpaid") }}</th>
                    <th>{{ $t("statistics.sales.refund") }}</th>
                    <th>{{ $t("statistics.sales.amount") }}</th>
                    <th>{{ $t("statistics.sales.total") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in trend" :key="'trend-' + index">
                    <td>{{ item.date || item.month }}</td>
                    <td>{{ formatCount(item.total_count) }}</td>
                    <td>{{ formatCount(item.paid_count) }}</td>
                    <td>{{ formatCount(item.unpaid_count) }}</td>
                    <td>{{ formatCount(item.refund_count) }}</td>
                    <td>{{ formatAmount(item.paid_amount) }}</td>
                    <td>{{ formatVolume(item.paid_volume) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.summary-card {
  transition: box-shadow 0.2s ease;
}

.summary-card:hover {
  box-shadow: 0 0.35rem 0.9rem rgba(0, 0, 0, 0.12) !important;
}
</style>
